/**
 * QL-349 — labels for rendering a filter's VALUE (not the field name — that's
 * `FilterField.label`, already localized by `buildFilterRegistry`). Shared by
 * the Settings view editor, the ad-hoc filter bar, and the view table's
 * summary badges, so a period/operator/empty name is spelled once.
 */

import { pickT } from "@repo/shared";
import type { DatePeriod } from "@repo/shared/types";
import type { ValuePickerLabels } from "@/components/ui/value-picker-dialog";
import { T } from "./value-labels.i18n";

export type FilterValueLabels = {
  noValuesSelected: string;
  selected: (count: number) => string;
  isEmpty: string;
  isNotEmpty: string;
  yes: string;
  no: string;
  favouriteHint: string;
  periodLabel: (period: DatePeriod) => string;
};

export function getFilterValueLabels(userLanguage: string): FilterValueLabels {
  const t = pickT(T, userLanguage);
  return {
    noValuesSelected: t.noValuesSelected,
    selected: (count) => t.selected(String(count)),
    isEmpty: t.isEmpty,
    isNotEmpty: t.isNotEmpty,
    yes: t.yes,
    no: t.no,
    favouriteHint: t.favouriteHint,
    periodLabel: (period) =>
      (
        ({
          today: t.today,
          this_week: t.thisWeek,
          this_month: t.thisMonth,
          this_quarter: t.thisQuarter,
          this_year: t.thisYear,
          older_than_month: t.olderThanMonth,
          older_than_quarter: t.olderThanQuarter,
          older_than_half_year: t.olderThanHalfYear,
          older_than_year: t.olderThanYear,
        }) satisfies Record<DatePeriod, string>
      )[period],
  };
}

/** Labels for `ValuePickerDialog` (the isAnyOf/containsAnyOf sub-dialog) — one definition, shared. */
export function valuePickerLabels(userLanguage: string, fieldLabel: string): ValuePickerLabels {
  const t = pickT(T, userLanguage);
  return {
    title: t.selectTitle(fieldLabel),
    description: (count) => t.selectDescription(String(count)),
  };
}
